import ExpandIcon from "@redotech/redo-icons/expand-01_filled.svg";
import MinimizeIcon from "@redotech/redo-icons/minimize-01_filled.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type {
  AircallLoginData,
  AircallWorkspace,
  AircallWorkspaceOptions,
} from "./aircall-types";
import * as aircallWidgetCss from "./aircall-widget.module.css";

const AIRCALL_CONTAINER_ID = "aircall-widget-container";

let aircallWorkspaceSingleton: AircallWorkspace | null = null;
let listenersAttached = false;

export interface AircallWidgetProps {
  onCallEnded?: (data: { duration: number; callId: number }) => void;
  onIncomingCall?: (data: { from: string; to: string; callId: number }) => void;
  onLogin?: (data: AircallLoginData) => void;
  onLogout?: () => void;
  onOutgoingCall?: (data: { from: string; to: string; callId: number }) => void;
}

export const AircallWidget = memo(function AircallWidget({
  onCallEnded,
  onIncomingCall,
  onLogin,
  onLogout,
  onOutgoingCall,
}: AircallWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveCall, setHasActiveCall] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use refs to avoid stale closures in singleton event handlers
  const onCallEndedRef = useRef(onCallEnded);
  const onIncomingCallRef = useRef(onIncomingCall);
  const onOutgoingCallRef = useRef(onOutgoingCall);
  const onLoginRef = useRef(onLogin);
  const onLogoutRef = useRef(onLogout);
  const setIsOpenRef = useRef(setIsOpen);
  const setHasActiveCallRef = useRef(setHasActiveCall);

  // Keep refs in sync with latest props
  useEffect(() => {
    onCallEndedRef.current = onCallEnded;
    onIncomingCallRef.current = onIncomingCall;
    onOutgoingCallRef.current = onOutgoingCall;
    onLoginRef.current = onLogin;
    onLogoutRef.current = onLogout;
  }, [onCallEnded, onIncomingCall, onOutgoingCall, onLogin, onLogout]);

  const handleLogin = useCallback((data: AircallLoginData) => {
    setIsLoggedIn(true);
    setIsLoading(false);
    onLoginRef.current?.(data);
  }, []);

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    onLogoutRef.current?.();
  }, []);

  useEffect(() => {
    if (aircallWorkspaceSingleton) {
      setIsLoading(false);
      // Attach listeners if not already attached (handles remount case)
      if (!listenersAttached) {
        attachEventListeners(aircallWorkspaceSingleton);
      }
      return;
    }

    const initAircall = async (): Promise<(() => void) | undefined> => {
      try {
        const AircallWorkspaceModule: { default: unknown } = await import(
          "aircall-everywhere"
        );
        const moduleDefault = AircallWorkspaceModule.default as
          | {
              default?: new (
                options: AircallWorkspaceOptions,
              ) => AircallWorkspace;
            }
          | (new (options: AircallWorkspaceOptions) => AircallWorkspace);
        const AircallWorkspaceClass = (
          moduleDefault &&
          typeof moduleDefault === "object" &&
          "default" in moduleDefault
            ? moduleDefault.default
            : moduleDefault
        ) as new (options: AircallWorkspaceOptions) => AircallWorkspace;

        const options: AircallWorkspaceOptions = {
          domToLoadWorkspace: `#${AIRCALL_CONTAINER_ID}`,
          onLogin: handleLogin,
          onLogout: handleLogout,
          size: "auto",
          debug: false,
        };

        aircallWorkspaceSingleton = new AircallWorkspaceClass(options);

        attachEventListeners(aircallWorkspaceSingleton);

        const loadingTimeout = setTimeout(() => {
          setIsLoading(false);
        }, 3000);

        return () => clearTimeout(loadingTimeout);
      } catch (error) {
        console.error("Failed to initialize Aircall widget:", error);
        setIsLoading(false);
        return undefined;
      }
    };

    function attachEventListeners(workspace: AircallWorkspace) {
      if (listenersAttached) {
        return;
      }

      workspace.on("incoming_call", (data) => {
        setIsOpenRef.current(true);
        setHasActiveCallRef.current(true);
        onIncomingCallRef.current?.({
          from: data.from,
          to: data.to,
          callId: data.call_id,
        });
      });

      workspace.on("outgoing_call", (data) => {
        setHasActiveCallRef.current(true);
        onOutgoingCallRef.current?.({
          from: data.from,
          to: data.to,
          callId: data.call_id,
        });
      });

      workspace.on("call_ended", (data) => {
        setHasActiveCallRef.current(false);
        onCallEndedRef.current?.({
          duration: data.duration,
          callId: data.call_id,
        });
      });

      listenersAttached = true;
    }

    void initAircall();

    return () => {
      if (aircallWorkspaceSingleton && listenersAttached) {
        aircallWorkspaceSingleton.removeListener("incoming_call");
        aircallWorkspaceSingleton.removeListener("outgoing_call");
        aircallWorkspaceSingleton.removeListener("call_ended");
        listenersAttached = false;
      }
    };
  }, [handleLogin, handleLogout]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const panelClassName = isExpanded
    ? `${aircallWidgetCss.widgetPanel} ${aircallWidgetCss.widgetPanelExpanded}`
    : aircallWidgetCss.widgetPanel;

  return (
    <>
      <div
        className={panelClassName}
        style={{ display: isOpen ? "flex" : "none" }}
      >
        <Flex
          align="center"
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          justify="space-between"
          p="md"
        >
          <Flex align="center" gap="sm">
            <PhoneIcon height={16} width={16} />
            <Text fontSize="sm" fontWeight="semibold">
              Phone
            </Text>
            {isLoggedIn && (
              <Text fontSize="xs" textColor="success">
                Connected
              </Text>
            )}
          </Flex>
          <Flex align="center" gap="xs">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={isExpanded ? MinimizeIcon : ExpandIcon}
              onClick={handleToggleExpand}
              size="xs"
            />
            <RedoButton
              hierarchy="tertiary"
              IconLeading={XIcon}
              onClick={handleToggle}
              size="xs"
            />
          </Flex>
        </Flex>
        <div className={aircallWidgetCss.widgetContent}>
          {isLoading && (
            <Flex
              align="center"
              className={aircallWidgetCss.loadingOverlay}
              justify="center"
            >
              <RedoSpinner size="md" />
            </Flex>
          )}
          <div
            className={aircallWidgetCss.aircallIframe}
            id={AIRCALL_CONTAINER_ID}
            ref={containerRef}
          />
        </div>
      </div>
      <div
        className={
          hasActiveCall
            ? `${aircallWidgetCss.fabContainer} ${aircallWidgetCss.fabRinging}`
            : aircallWidgetCss.fabContainer
        }
      >
        <RedoButton
          hierarchy={hasActiveCall || isOpen ? "primary" : "secondary"}
          IconLeading={PhoneIcon}
          onClick={handleToggle}
          size="lg"
          theme={hasActiveCall ? "success" : "normal"}
        />
      </div>
    </>
  );
});
